// src/components/marketing/tabs/LeadsTab.jsx
import { useState, useMemo } from 'react';
import { useLeads, MARKETING_SOURCES } from '../../../context/LeadsContext';
import { useCustomers } from '../../../context/CustomerContext';
import { useAuditLog } from '../../../context/AuditLogContext';
import { useRoles, ROLES } from '../../../context/RolesContext';
import { useNotifications } from '../../../context/NotificationsContext';
import { analyzeCustomerPersonality } from '../../../lib/personalityEngine';
import LeadDetailPanel from '../../leadhub/LeadDetailPanel';
import NewLeadModal from '../../leadhub/NewLeadModal';

// ─── Source metadata ──────────────────────────────────────────────────────────

const SOURCE_META = {
  facebook:  { label: 'Facebook',  color: '#1877F2' },
  instagram: { label: 'Instagram', color: '#EA4397' },
  google:    { label: 'Google',    color: '#4285F4' },
  yelp:      { label: 'Yelp',      color: '#D32323' },
  website:   { label: 'Website',   color: '#7D93AE' },
};

// ─── Small status badge ───────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = {
    new:       { label: 'New',       cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    contacted: { label: 'Contacted', cls: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    quoted:    { label: 'Quoted',    cls: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    scheduled: { label: 'Scheduled', cls: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    won:       { label: 'Won',       cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    lost:      { label: 'Lost',      cls: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  }[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`text-[var(--wm-text-micro)] font-semibold px-2 py-0.5 rounded-full ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LeadsTab({ onNavigate }) {
  const { marketingLeads, setLeads } = useLeads();
  const { customers }                = useCustomers();
  const { addLog }                   = useAuditLog();
  const { currentRole }              = useRoles();
  const { addNotification }          = useNotifications();
  const actor = { role: currentRole, label: ROLES[currentRole]?.label || currentRole };

  const [activeSource, setActiveSource] = useState(null); // null = show all
  const [detailLead,   setDetailLead]   = useState(null);
  const [newLeadOpen,  setNewLeadOpen]  = useState(false);

  // Per-source stats for the performance cards
  const sourceStats = useMemo(() =>
    MARKETING_SOURCES.map(src => {
      const srcLeads = marketingLeads.filter(l => l.source === src);
      const won      = srcLeads.filter(l => l.status === 'won').length;
      return {
        source:  src,
        count:   srcLeads.length,
        wonRate: srcLeads.length > 0 ? Math.round((won / srcLeads.length) * 100) : 0,
      };
    }),
  [marketingLeads]);

  // Leads shown in the table (filtered by active source card, if any)
  const displayedLeads = useMemo(() =>
    activeSource
      ? marketingLeads.filter(l => l.source === activeSource)
      : marketingLeads,
  [marketingLeads, activeSource]);

  // DISC personality for the detail panel (same logic as LeadHubPage)
  const detailPersonality = useMemo(() => {
    if (!detailLead) return null;
    const match = customers.find(c =>
      (detailLead.email && c.email?.toLowerCase() === detailLead.email?.toLowerCase()) ||
      (detailLead.phone && c.phone?.replace(/\D/g, '') === detailLead.phone?.replace(/\D/g, ''))
    );
    if (match?.personality) return match.personality;
    return analyzeCustomerPersonality({ name: detailLead.name }, [], [], []);
  }, [detailLead, customers]);

  // ─── Handlers (same mutation pattern as LeadHubPage) ───────────────────────

  const handleSaveLead = (updated) => {
    setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
    setDetailLead(updated);
    addLog('DATA', 'LEAD_UPDATED', { severity: 'info', actor, target: updated.name, details: {} });
  };

  const handleDelete = (lead) => {
    setLeads(prev => prev.filter(l => l.id !== lead.id));
    if (detailLead?.id === lead.id) setDetailLead(null);
    addLog('DATA', 'LEAD_DELETED', { severity: 'critical', actor, target: lead.name, details: {} });
  };

  const handleArchive = (lead) => {
    setLeads(prev => prev.filter(l => l.id !== lead.id));
    if (detailLead?.id === lead.id) setDetailLead(null);
    addLog('DATA', 'LEAD_ARCHIVED', { severity: 'warning', actor, target: lead.name, details: {} });
  };

  const handleConvert = (lead) => {
    setLeads(prev => prev.map(l =>
      l.id === lead.id ? { ...l, status: 'won', wonAt: new Date().toISOString() } : l
    ));
    addNotification({
      type: 'lead', title: 'Lead Converted',
      body: `${lead.name} marked won from Marketing`,
      link: 'leadhub', icon: 'target',
    });
    addLog('DATA', 'LEAD_CONVERTED_TO_ESTIMATE', { severity: 'success', actor, target: lead.name, details: {} });
  };

  const handleAddActivity = (leadId, activity) => {
    setLeads(prev => prev.map(l =>
      l.id === leadId ? { ...l, activities: [...(l.activities || []), activity] } : l
    ));
  };

  const handleCreateLead = (lead) => {
    setLeads(prev => [lead, ...prev]);
    addLog('DATA', 'LEAD_CREATED', { severity: 'success', actor, target: lead.name, details: {} });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">
            Marketing Leads
          </h2>
          <span className="bg-[#2E8BF0]/10 text-[#2E8BF0] text-[var(--wm-text-caption)] px-2 py-0.5 rounded-full font-semibold">
            {marketingLeads.length} total
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate?.('leadhub')}
            className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors"
          >
            View full pipeline →
          </button>
          <button
            onClick={() => setNewLeadOpen(true)}
            className="px-3 py-1.5 text-[var(--wm-text-ui)] font-semibold wm-btn-primary transition-colors"
          >
            + Add Lead
          </button>
        </div>
      </div>

      {/* Source performance cards */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {sourceStats.map(({ source, count, wonRate }) => {
          const meta     = SOURCE_META[source];
          const isActive = activeSource === source;
          return (
            <button
              key={source}
              onClick={() => setActiveSource(isActive ? null : source)}
              className="min-w-[110px] flex-shrink-0 text-left rounded-xl p-3 transition-all border bg-[var(--wm-bg-secondary)]"
              style={{
                borderColor: isActive ? meta.color : 'var(--wm-bg-border)',
                boxShadow:   isActive ? `0 0 0 1px ${meta.color}` : 'none',
              }}
            >
              <div
                className="text-[var(--wm-text-micro)] font-bold uppercase tracking-widest mb-1"
                style={{ color: meta.color }}
              >
                ● {meta.label}
              </div>
              <div className="text-[var(--wm-text-hero)] font-bold text-[#0F1923] dark:text-[#F8FAFE]">{count}</div>
              <div className="text-[var(--wm-text-micro)] text-[#64748B] dark:text-[#7D93AE]">leads</div>
              <div className="mt-2 h-1 rounded-full bg-gray-100 dark:bg-[#243348] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${wonRate}%`, background: '#22C55E' }}
                />
              </div>
              <div
                className="text-[var(--wm-text-micro)] mt-1"
                style={{ color: wonRate > 0 ? '#22C55E' : '#7D93AE' }}
              >
                {wonRate}% won
              </div>
            </button>
          );
        })}
      </div>

      {/* Active filter pill */}
      {activeSource && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveSource(null)}
            className="text-[var(--wm-text-caption)] font-semibold px-3 py-1 rounded-full border transition-colors"
            style={{
              color:       SOURCE_META[activeSource].color,
              borderColor: SOURCE_META[activeSource].color + '44',
              background:  SOURCE_META[activeSource].color + '15',
            }}
          >
            {SOURCE_META[activeSource].label} ×
          </button>
          <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">{displayedLeads.length} leads</span>
        </div>
      )}

      {/* Lead list */}
      <div className="bg-[var(--wm-bg-secondary)] border border-[var(--wm-bg-border)] rounded-xl overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-[2fr_1.3fr_1fr_90px_75px] px-4 py-2.5 bg-[var(--wm-bg-border)]/40">
          {['Name', 'Vehicle', 'Service', 'Status', 'Budget'].map(h => (
            <span key={h} className="text-[var(--wm-text-micro)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE]">
              {h}
            </span>
          ))}
        </div>

        {/* Data rows */}
        <div className="divide-y divide-[var(--wm-bg-border)]">
          {displayedLeads.map(lead => (
            <button
              key={lead.id}
              onClick={() => setDetailLead(lead)}
              className="w-full grid grid-cols-[2fr_1.3fr_1fr_90px_75px] px-4 py-3 text-left hover:bg-[var(--wm-bg-primary)] transition-colors"
            >
              <div>
                <div className="text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">{lead.name}</div>
                <div className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">{lead.email}</div>
              </div>
              <div className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] self-center">
                {[lead.vehicle?.year, lead.vehicle?.make, lead.vehicle?.model].filter(Boolean).join(' ') || '—'}
              </div>
              <div className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] self-center truncate">
                {lead.serviceInterest || '—'}
              </div>
              <div className="self-center">
                <StatusBadge status={lead.status} />
              </div>
              <div className="text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] self-center">
                {lead.budget ? `$${Number(lead.budget).toLocaleString()}` : '—'}
              </div>
            </button>
          ))}

          {displayedLeads.length === 0 && (
            <div className="px-4 py-10 text-center text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
              {activeSource
                ? `No ${SOURCE_META[activeSource].label} leads yet.`
                : 'No marketing leads yet. Add one or check that leads have a marketing source (facebook, instagram, google, yelp, website).'}
            </div>
          )}
        </div>
      </div>

      {/* Lead detail slide-over (reuses LeadHub's full panel) */}
      <LeadDetailPanel
        lead={detailLead}
        open={!!detailLead}
        onClose={() => setDetailLead(null)}
        onSave={handleSaveLead}
        onDelete={handleDelete}
        onArchive={handleArchive}
        onConvert={handleConvert}
        onAddActivity={handleAddActivity}
        onScheduleConsultation={null}
        personality={detailPersonality}
      />

      {/* New lead modal — source list filtered to marketing sources */}
      <NewLeadModal
        open={newLeadOpen}
        onClose={() => setNewLeadOpen(false)}
        onCreate={handleCreateLead}
        sourcesAllowed={MARKETING_SOURCES}
      />

    </div>
  );
}
